import { CardActions } from '@mobile/screens/card-details/card-actions'
import { CardSummary, type CardSummaryProps } from '@mobile/screens/card-details/card-summary'
import { useCardCarouselMouse } from '@mobile/screens/card-details/use-card-carousel-mouse'
import type { useCardSheet } from '@mobile/screens/card-details/use-card-sheet'
import {
  type BackendCard,
  type CardTemplateId,
  isBlockedCardStatus,
  isFrozenCardStatus,
  matchesCardTemplate,
} from '@shared-core/features/backend'
import { useEvent } from '@shared-core/hooks/use-event'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { AppBankCard } from '@shared-ui/components/app/bank-card'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Row } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { useAppTheme } from '@shared-ui/theme'
import { rh, rw } from '@shared-ui/theme/scale'
import { type ComponentRef, memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Animated, Easing as NativeEasing, Pressable, ScrollView, useWindowDimensions } from 'react-native'
import Reanimated, { Easing, FadeInDown, FadeOut } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

// Ширина шага карусели и ширина видимой карты в базовых единицах дизайна.
const CARD_STEP_DESIGN_PX = 296
const CARD_VISIBLE_DESIGN_PX = 264
const CARD_SCROLL_DURATION_MS = 320

const CARD_CONTENT_ENTERING = FadeInDown.duration(280)
  .easing(Easing.out(Easing.cubic))
  .withInitialValues({ opacity: 0, transform: [{ translateY: rh(12) }] })
const AnimatedCardScrollView = Animated.createAnimatedComponent(ScrollView)

type CardPreviewProps = {
  type: CardTemplateId
  lastDigits: string
  isDetails: boolean
  historyOpacity: Animated.AnimatedInterpolation<number>
  detailsOpacity: Animated.AnimatedInterpolation<number>
  onOpen: (type: CardTemplateId) => void
  isBlocked: boolean
  isFrozen: boolean
}

// onOpen стабилен между рендерами, поэтому memo действительно пропускает неизменённые карты.
const CardPreview = memo(function CardPreviewComponent({
  type,
  lastDigits,
  isDetails,
  historyOpacity,
  detailsOpacity,
  onOpen,
  isBlocked,
  isFrozen,
}: CardPreviewProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const theme = useAppTheme()
  const handlePress = useEvent(() => onOpen(type))
  const color = type === 'platinum' ? theme.COLOR_NEUTRAL_50 : theme.COLOR_NEUTRAL_950

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={t('requisites')}
      disabled={isDetails}
      onPress={handlePress}
      style={styles.card}
    >
      <AppBankCard
        type={type}
        size="middle"
        isNumberVisible={false}
        isCloudVisible={false}
        isBlocked={isBlocked}
        isFrozen={isFrozen}
      />
      <Animated.View pointerEvents="none" style={[styles.cardCoverLayer, { opacity: detailsOpacity }]}>
        <AppBankCard type={type} size="middle" isNumberVisible={false} isFrozen={isFrozen} />
      </Animated.View>
      <Animated.View pointerEvents="none" style={[styles.cardOverlay, { opacity: historyOpacity }]}>
        {!isBlocked && (
          <Row align="center" gap={4}>
            <GText style={[styles.cardNumberBullet, { color }]}>{lastDigits ? '•' : ''}</GText>
            <GText fw={500} style={[styles.cardNumber, { color }]}>
              {lastDigits}
            </GText>
          </Row>
        )}
        <Row align="center" gap={4}>
          <GText fw={500} style={[styles.cardDetailsLabel, { color }]}>
            {t('requisites')}
          </GText>
          <AppIcon icon="chevron-right" size={16} color={color} />
        </Row>
      </Animated.View>
    </Pressable>
  )
})

type CardSummaryTransitionProps = {
  sheet: Pick<ReturnType<typeof useCardSheet>, 'isDetails' | 'progress' | 'historyOpacity'>
  summary: CardSummaryProps
}

export function CardSummaryTransition({ sheet, summary }: CardSummaryTransitionProps) {
  const styles = useCreateStyles()
  return (
    <Animated.View
      pointerEvents={sheet.isDetails ? 'none' : 'auto'}
      style={[
        styles.summaryAnimation,
        {
          opacity: sheet.historyOpacity,
          height: sheet.progress.interpolate({ inputRange: [0, 1], outputRange: [rh(177), 0] }),
        },
      ]}
    >
      <CardSummary {...summary} />
    </Animated.View>
  )
}

type CardDetailsActionsProps = {
  isDetails: boolean
  detailsOpacity: ReturnType<typeof useCardSheet>['detailsOpacity']
  onBlock: () => void
  onPin: () => void
  onFreeze: () => void
  isFrozen: boolean
  isLoading: boolean
}

export function CardDetailsActions({
  isDetails,
  detailsOpacity,
  onBlock,
  onFreeze,
  onPin,
  isFrozen,
  isLoading,
}: CardDetailsActionsProps) {
  const styles = useCreateStyles()
  return (
    <Animated.View
      pointerEvents={isDetails ? 'auto' : 'none'}
      style={[styles.cardDetailsActions, { opacity: detailsOpacity }]}
    >
      <Reanimated.View entering={CARD_CONTENT_ENTERING}>
        <CardActions onPin={onPin} onBlock={onBlock} onFreeze={onFreeze} isFrozen={isFrozen} isLoading={isLoading} />
      </Reanimated.View>
    </Animated.View>
  )
}

type CardCarouselProps = {
  cardTypes: readonly CardTemplateId[]
  issuedCards: BackendCard[]
  selection: { type: CardTemplateId; lastDigits: string }
  sheet: Pick<ReturnType<typeof useCardSheet>, 'isDetails' | 'historyOpacity' | 'detailsOpacity'>
  carouselRef: React.RefObject<ComponentRef<typeof ScrollView> | null>
  actions: {
    onSelect: (type: CardTemplateId) => void
    onOpen: (type: CardTemplateId) => void
    onReturned: () => void
  }
}

export function CardCarousel({ cardTypes, issuedCards, selection, sheet, carouselRef, actions }: CardCarouselProps) {
  const cardStep = rw(CARD_STEP_DESIGN_PX)
  const cardVisibleWidth = rw(CARD_VISIBLE_DESIGN_PX)
  useCardCarouselMouse(carouselRef, cardStep, (index) => {
    const type = cardTypes[Math.max(0, Math.min(index, cardTypes.length - 1))]
    actions.onSelect(type)
  })
  const styles = useCreateStyles()
  const window = useWindowDimensions()
  const [width, setWidth] = useState(window.width)
  const arrival = useRef(new Animated.Value(0)).current
  const initialIndexRef = useRef(Math.max(0, cardTypes.indexOf(selection.type)))
  const hasRestoredPositionRef = useRef(false)

  // Данные каждой обложки считаем один раз на изменение списка, а не на каждый проход по карте.
  const previews = useMemo(
    () =>
      cardTypes.map((type) => {
        const typeCards = issuedCards.filter((card) => matchesCardTemplate(card, type))
        const lastDigits =
          typeCards[0]?.maskedPan.replace(/ /g, '').slice(-4) || (type === selection.type ? selection.lastDigits : '')

        return {
          type,
          lastDigits,
          isBlocked: !sheet.isDetails && typeCards.some((card) => isBlockedCardStatus(card.status)),
          isFrozen: typeCards.some((card) => isFrozenCardStatus(card.status)),
        }
      }),
    [cardTypes, issuedCards, selection.type, selection.lastDigits, sheet.isDetails],
  )

  // Появление запускаем только после выставления позиции, иначе виден кадр с первой картой и скачок.
  const startArrival = useEvent(() => {
    Animated.timing(arrival, {
      toValue: 1,
      duration: 260,
      easing: NativeEasing.out(NativeEasing.cubic),
      useNativeDriver: true,
    }).start()
  })

  useEffect(() => {
    // Запасной запуск, если измерение карусели не пришло.
    const fallback = setTimeout(startArrival, 150)
    return () => {
      clearTimeout(fallback)
      arrival.stopAnimation()
    }
  }, [arrival, startArrival])

  const selectedTypeRef = useRef(selection.type)

  selectedTypeRef.current = selection.type

  const offsetRef = useRef(0)
  const isProgrammaticScrollRef = useRef(false)
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([])

  useEffect(() => {
    const timers = timersRef.current

    return () => timers.forEach(clearTimeout)
  }, [])

  // Раскрытие деталей добавляет обложки слева от выбранной: возвращаем её в центр до отрисовки кадра.
  const previousCardTypesRef = useRef(cardTypes)

  useLayoutEffect(() => {
    if (previousCardTypesRef.current === cardTypes) return

    previousCardTypesRef.current = cardTypes

    if (!hasRestoredPositionRef.current) return

    const index = cardTypes.indexOf(selectedTypeRef.current)

    if (index >= 0) carouselRef.current?.scrollTo({ x: cardStep * index, animated: false })
  }, [cardTypes, cardStep, carouselRef])

  // Выбор типа снаружи (например, возврат после пополнения) докручивает карусель до нужной карты.
  useEffect(() => {
    if (!hasRestoredPositionRef.current) return

    const index = cardTypes.indexOf(selection.type)

    if (index < 0 || Math.abs(offsetRef.current - index * cardStep) < 2) return

    carouselRef.current?.scrollTo({ x: cardStep * index, animated: true })
  }, [selection.type])

  // Во время программной прокрутки onScroll не должен выбирать карты, мимо которых проезжает карусель.
  const handleOpen = useEvent((type: CardTemplateId) => {
    const index = cardTypes.indexOf(type)

    if (index < 0 || Math.abs(offsetRef.current - index * cardStep) < 2) {
      actions.onOpen(type)

      return
    }

    isProgrammaticScrollRef.current = true
    actions.onSelect(type)
    carouselRef.current?.scrollTo({ x: cardStep * index, animated: true })
    timersRef.current.push(
      setTimeout(() => {
        isProgrammaticScrollRef.current = false
        actions.onOpen(type)
      }, CARD_SCROLL_DURATION_MS),
    )
  })

  const restorePosition = () => {
    if (hasRestoredPositionRef.current) return

    carouselRef.current?.scrollTo({ x: cardStep * initialIndexRef.current, animated: false })
    hasRestoredPositionRef.current = true
    requestAnimationFrame(startArrival)
  }

  return (
    <AnimatedCardScrollView
      ref={carouselRef}
      onLayout={(event) => {
        setWidth(event.nativeEvent.layout.width)
      }}
      horizontal
      onContentSizeChange={restorePosition}
      contentOffset={{ x: cardStep * initialIndexRef.current, y: 0 }}
      style={[
        styles.carouselViewport,
        {
          opacity: arrival.interpolate({ inputRange: [0, 1], outputRange: [0, 1] }),
          transform: [{ translateY: arrival.interpolate({ inputRange: [0, 1], outputRange: [rh(10), 0] }) }],
        },
      ]}
      scrollEventThrottle={16}
      onScroll={(event) => {
        const offset = event.nativeEvent.contentOffset.x

        offsetRef.current = offset

        if (!hasRestoredPositionRef.current || isProgrammaticScrollRef.current) return

        const index = Math.max(0, Math.min(Math.round(offset / cardStep), cardTypes.length - 1))

        if (Math.abs(offset) < 1) actions.onReturned()

        // На web окончание инерции приходит не всегда: выбираем карту при её центрировании.
        if (Math.abs(offset - index * cardStep) < 2) actions.onSelect(cardTypes[index])
      }}
      onMomentumScrollEnd={(event) => {
        if (isProgrammaticScrollRef.current) return

        const index = Math.max(
          0,
          Math.min(Math.round(event.nativeEvent.contentOffset.x / cardStep), cardTypes.length - 1),
        )
        actions.onSelect(cardTypes[index])
      }}
      snapToInterval={cardStep}
      snapToAlignment="start"
      decelerationRate="fast"
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={[styles.cardCarousel, { paddingHorizontal: Math.max(0, (width - cardVisibleWidth) / 2) }]}
    >
      {previews.map((preview) => (
        <Reanimated.View key={preview.type} exiting={FadeOut.duration(220)}>
          <CardPreview
            type={preview.type}
            lastDigits={preview.lastDigits}
            isBlocked={preview.isBlocked}
            isFrozen={preview.isFrozen}
            {...sheet}
            onOpen={handleOpen}
          />
        </Reanimated.View>
      ))}
    </AnimatedCardScrollView>
  )
}
